import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { FieldConfiguration } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  DimensionField,
  DraggableFieldType,
  DraggableMetaType,
  FieldWrapper,
  MetaDescriptorBase,
} from "../../../../../../../../../api/types/svTypes.ts";
import { AreaItemType, ItemDataType, MetaItemType } from "../../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch";
import { selectArguments } from "../../../../../store/selectors/fieldsStateSelectors";
import { fieldsStateActions } from "../../../../../store/thunks/fieldStatesThunks";
import { isDimensionBased } from "../../../../../utilities/fieldsHelper.ts";
import type { ShowFieldOptionsSettings } from "../../../../viewer/common/Types.ts";
import DropFieldContainer from "../../../common/fields/DropFieldContainer.tsx";
import { createArgument } from "../../../common/utilities/createFields.ts";
import { extractMeta } from "../../../common/utilities/dropFieldContainerHelper.ts";
import GeneralFieldOptionPopup from "../../../pivot/table/columns/GeneralFieldOptionPopup.tsx";
import { formatDimensionFieldCaption } from "../../../utils/formatDimensionFieldCaptions.tsx";

const MAX_FIELDS = 1;

interface Props {
  readonlyMode?: boolean;
}
export const ArgumentDropFieldsContainer = ({ readonlyMode = false }: Props) => {
  const argumentsAreaValues = useSelector(selectArguments);
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [field, setField] = useState<DimensionField>();

  const argumentsRef = useRef<DimensionField[]>([]);
  useEffect(() => {
    argumentsRef.current = argumentsAreaValues;
  }, [argumentsAreaValues]);
  useEffect(() => {
    const row = argumentsAreaValues.find((v) => v.meta.name === field?.meta.name);
    // TODO #209
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setField(row);
  }, [field, argumentsAreaValues]);
  const handleShowOptions = useCallback((settings: ShowFieldOptionsSettings<DimensionField>) => {
    setField(settings.field);
    setAnchorEl(settings.ref);
  }, []);

  const canDropArgumentItem = useCallback((item: unknown) => {
    const field = item as MetaDescriptorBase;
    if (argumentsRef.current.length >= MAX_FIELDS) {
      return false;
    }
    if (field.type === ItemDataType.Date) {
      const anyDateFieldAdded = !!argumentsRef.current?.find((f) => f.meta.type === ItemDataType.Date);
      return anyDateFieldAdded === false;
    }
    return !argumentsRef.current.some((v) => v.meta.name === field.name);
  }, []);

  const onClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const updateItemConfig = useCallback(
    (field: DimensionField, changes: Partial<FieldConfiguration>) => {
      dispatch(fieldsStateActions.updateArgumentConfig({ field, changes }));
    },
    [dispatch]
  );

  const removeItem = useCallback(
    (field: DimensionField) => {
      dispatch(fieldsStateActions.removeArgument(field));
    },
    [dispatch]
  );

  const moveItem = useCallback(
    (field: DimensionField, toIndex: number) => {
      dispatch(fieldsStateActions.moveArgument({ field, newIndex: toIndex }));
    },
    [dispatch]
  );

  const addItem = useCallback(
    (field: DimensionField, index: number) => {
      dispatch(fieldsStateActions.addArgument({ field, index }));
    },
    [dispatch]
  );

  const saveChanges = useCallback(
    (changes: Partial<FieldConfiguration>) => {
      if (isDimensionBased(field)) {
        updateItemConfig(field, changes);
      }
    },
    [updateItemConfig, field]
  );

  const handleSaveChanges = useCallback(
    (changes: Partial<FieldConfiguration>, causeClosing: boolean) => {
      saveChanges(changes);
      if (causeClosing) {
        onClose();
      }
    },
    [onClose, saveChanges]
  );

  const onRemove = useCallback(() => {
    if (isDimensionBased(field)) {
      removeItem(field);
    }
    setAnchorEl(null);
  }, [removeItem, field]);

  const handleArgumentFieldCustomLabelChange = (name: string | undefined) => {
    if (field) {
      updateItemConfig(field, { customLabel: name });
    }
  };

  return (
    <Box
      sx={() => ({
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
      })}
    >
      <DropFieldContainer
        title="Arguments"
        maxFields={MAX_FIELDS}
        headingActions={
          <Typography variant="subtitle2" color="textDisabled">
            (Dimensions)
          </Typography>
        }
        areaFieldType={AreaItemType.ARGUMENTS}
        acceptedDropTypes={[MetaItemType.DIMENSION, AreaItemType.CONDITIONS]}
        fields={argumentsAreaValues}
        getKeyValue={getKeyValue}
        formatCaption={formatDimensionFieldCaption}
        canDropItem={canDropArgumentItem}
        onAddItem={addItem}
        onRemoveItem={removeItem}
        onMoveItem={moveItem}
        showOptions={handleShowOptions}
        createListItem={createArgument}
        readonly={readonlyMode}
      />
      {anchorEl && field && (
        <GeneralFieldOptionPopup
          anchorEl={anchorEl}
          field={field}
          onUpdateConfiguration={handleSaveChanges}
          cancel={onClose}
          onRemove={onRemove}
          onUpdateCustomLabel={handleArgumentFieldCustomLabelChange}
          readonly={readonlyMode}
        />
      )}
    </Box>
  );
};

export default ArgumentDropFieldsContainer;

function getKeyValue(field: FieldWrapper<DraggableFieldType> | DraggableMetaType) {
  return extractMeta(field)?.name ?? "";
}
